import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Search, Building2, User as UserIcon, X, CheckCircle2, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';

const toLocalDateStr = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const todayStr = () => toLocalDateStr(new Date());

// Mirrors backend `is_package_usable` (routes/day_pass_packages.py) closely
// enough for UI purposes — the server re-validates authoritatively on submit.
const isPackageUsable = (pkg, now) => {
  if (pkg.status !== 'active') return false;
  if ((pkg.quantity_remaining || 0) <= 0) return false;
  if (pkg.start_date && new Date(pkg.start_date) > now) return false;
  if (pkg.expiry_date && new Date(pkg.expiry_date) < now) return false;
  return true;
};

// Front-desk action for a client who forgot to book and just walked in: pick
// them (either search here, or arrive pre-selected via `initialClient` from
// clicking their name in the packages table), confirm which of the CM's own
// centre(s) they're at, and deduct however many passes they actually used
// from their existing Bulk Day Pass balance. Submits with force_day_pass so
// the booking fails loudly rather than silently falling back to charging
// credits/money if they don't actually have a usable pass.
export const MarkDayPassUsedModal = ({ open, onClose, api, myVenues = [], onMarked, initialClient = null }) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState({ users: [], organizations: [] });
  const [selectedClient, setSelectedClient] = useState(null); // { type: 'user'|'organization', id, name, email }
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [clientPackages, setClientPackages] = useState([]);
  const [dayPassBookables, setDayPassBookables] = useState([]);
  const [venueId, setVenueId] = useState('');
  const [date, setDate] = useState(todayStr());
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery(''); setResults({ users: [], organizations: [] }); setSelectedClient(null);
      setClientPackages([]); setVenueId(''); setDate(todayStr()); setQuantity(1);
    } else if (initialClient) {
      setSelectedClient(initialClient);
    }
  }, [open, initialClient]);

  useEffect(() => {
    if (!open || selectedClient) return;
    const q = query.trim();
    if (q.length < 2) { setResults({ users: [], organizations: [] }); return; }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/day-pass-packages/client-search', { params: { q } });
        setResults(res.data || { users: [], organizations: [] });
      } catch (e) {
        // silent — typeahead, not critical
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query, open, selectedClient, api]);

  useEffect(() => {
    if (!open) return;
    // include_inactive so a venue whose Day Pass space is closed to public
    // (external) booking still shows up here — the backend's internal-client
    // endpoint allows the booking as long as is_active OR is_active_internal
    // is true (see create_internal_client_booking in routes/bookings.py), so
    // this CM walk-in flow must apply the same rule instead of the stricter
    // is_active-only filter the public booking flow uses.
    api.get('/bookables', { params: { bookable_type: 'day_pass', include_inactive: true } })
      .then((res) => {
        const all = Array.isArray(res.data) ? res.data : [];
        setDayPassBookables(all.filter((b) => b.is_active !== false || b.is_active_internal !== false));
      })
      .catch(() => setDayPassBookables([]));
  }, [open, api]);

  useEffect(() => {
    if (!selectedClient) return;
    setLoadingBalance(true);
    setVenueId('');
    const params = selectedClient.type === 'organization'
      ? { organization_id: selectedClient.id }
      : { user_id: selectedClient.id };
    api.get('/day-pass-packages', { params })
      .then((res) => setClientPackages(Array.isArray(res.data) ? res.data : []))
      .catch(() => setClientPackages([]))
      .finally(() => setLoadingBalance(false));
  }, [selectedClient, api]);

  const usablePackages = useMemo(
    () => clientPackages.filter((p) => isPackageUsable(p, new Date())),
    [clientPackages]
  );
  const unscopedRemaining = usablePackages
    .filter((p) => !p.venue_ids || p.venue_ids.length === 0)
    .reduce((s, p) => s + (p.quantity_remaining || 0), 0);
  const scopedVenueIds = useMemo(() => {
    const set = new Set();
    usablePackages.forEach((p) => (p.venue_ids || []).forEach((v) => set.add(v)));
    return set;
  }, [usablePackages]);
  const dayPassBookableVenueIds = useMemo(
    () => new Set(dayPassBookables.map((b) => b.venue_id)),
    [dayPassBookables]
  );
  // Only the CM's own centre(s) — marking a pass used means the client is
  // physically standing at one of those, not anywhere the package is scoped to.
  const eligibleVenueIds = useMemo(() => {
    const myVenueIds = myVenues.map((v) => v.id).filter((v) => dayPassBookableVenueIds.has(v));
    if (unscopedRemaining > 0) return myVenueIds;
    return myVenueIds.filter((v) => scopedVenueIds.has(v));
  }, [myVenues, dayPassBookableVenueIds, unscopedRemaining, scopedVenueIds]);

  useEffect(() => {
    if (selectedClient && eligibleVenueIds.length === 1 && !venueId) {
      setVenueId(eligibleVenueIds[0]);
    }
  }, [selectedClient, eligibleVenueIds, venueId]);

  const remainingAtVenue = (vId) => {
    const scoped = usablePackages
      .filter((p) => (p.venue_ids || []).includes(vId))
      .reduce((s, p) => s + (p.quantity_remaining || 0), 0);
    return scoped + unscopedRemaining;
  };

  const venueName = (id) => myVenues.find((v) => v.id === id)?.name || id;
  const balanceAtVenue = venueId ? remainingAtVenue(venueId) : 0;

  // Balance changes with the selected centre — keep quantity in bounds rather
  // than letting a stale value from a previous centre slip through.
  useEffect(() => {
    setQuantity((q) => Math.max(1, Math.min(q, balanceAtVenue || 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, balanceAtVenue]);

  const submit = async () => {
    if (!selectedClient) { toast.error('Search for and select a client'); return; }
    if (!venueId) { toast.error('Select a centre'); return; }
    if (balanceAtVenue <= 0) { toast.error('This client has no Day Pass balance at this centre'); return; }
    const qty = Math.max(1, Math.min(parseInt(quantity, 10) || 1, balanceAtVenue));
    const bookable = dayPassBookables.find((b) => b.venue_id === venueId);
    if (!bookable) { toast.error('No Day Pass space configured at this centre'); return; }

    setSubmitting(true);
    try {
      const payload = {
        bookable_id: bookable.id,
        venue_id: venueId,
        booking_date: date,
        start_time: '09:00',
        end_time: '20:00',
        attendees: qty,
        pass_quantity: qty,
        notes: 'Walk-in — marked as used by Centre Manager',
        force_day_pass: true,
      };
      if (selectedClient.type === 'organization') payload.organization_id = selectedClient.id;
      else payload.user_id = selectedClient.id;

      await api.post('/bookings/internal-client', payload);
      toast.success(`Marked ${qty} Day Pass${qty !== 1 ? 'es' : ''} as used for ${selectedClient.name}`);
      onMarked?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark pass as used');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="mark-day-pass-used-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-primary" /> Mark Day Pass(es) as Used</DialogTitle>
          <p className="text-sm text-muted-foreground">For a client who forgot to book and walked in — deducts passes from their existing balance.</p>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label>Client *</Label>
            {selectedClient ? (
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 border border-border mt-1">
                <div className="flex items-center gap-2 min-w-0">
                  {selectedClient.type === 'organization' ? <Building2 className="w-4 h-4 text-primary shrink-0" /> : <UserIcon className="w-4 h-4 text-muted-foreground shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{selectedClient.name}</p>
                    {selectedClient.email && <p className="text-xs text-muted-foreground truncate">{selectedClient.email}</p>}
                  </div>
                </div>
                <Button type="button" size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => setSelectedClient(null)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search client by name, email, or organization…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="mark-used-client-search"
                />
                {searching && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
                {(results.users?.length > 0 || results.organizations?.length > 0) && (
                  <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-border bg-popover shadow-md">
                    {results.organizations?.map((o) => (
                      <button
                        key={`org-${o.id}`}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                        onClick={() => { setSelectedClient({ type: 'organization', id: o.id, name: o.name, email: o.email }); setQuery(''); }}
                      >
                        <Building2 className="w-3.5 h-3.5 text-primary" /> {o.name}
                        <Badge variant="outline" className="ml-auto text-xs">Org</Badge>
                      </button>
                    ))}
                    {results.users?.map((u) => (
                      <button
                        key={`user-${u.id}`}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                        onClick={() => { setSelectedClient({ type: 'user', id: u.id, name: u.name, email: u.email }); setQuery(''); }}
                      >
                        <UserIcon className="w-3.5 h-3.5 text-muted-foreground" /> {u.name}
                        <span className="ml-auto text-xs text-muted-foreground truncate">{u.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedClient && (
            loadingBalance ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            ) : eligibleVenueIds.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">This client has no Day Pass balance at your centre(s).</p>
            ) : (
              <>
                {eligibleVenueIds.length > 1 && (
                  <div>
                    <Label>Centre *</Label>
                    <Select value={venueId} onValueChange={setVenueId}>
                      <SelectTrigger data-testid="mark-used-venue-select"><SelectValue placeholder="Select a centre" /></SelectTrigger>
                      <SelectContent>
                        {eligibleVenueIds.map((vId) => (
                          <SelectItem key={vId} value={vId}>{venueName(vId)} ({remainingAtVenue(vId)} passes)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {venueId && (
                  <>
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 text-sm">
                      <span className="font-semibold">{balanceAtVenue}</span> pass{balanceAtVenue !== 1 ? 'es' : ''} remaining at {venueName(venueId)}
                    </div>
                    <div>
                      <Label>Date *</Label>
                      <Input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="mark-used-date-input" />
                    </div>
                    <div>
                      <Label>Day Passes Used *</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                          className="w-8 h-8 rounded-md border border-border flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-40"
                          disabled={quantity <= 1}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <Input
                          type="number" min={1} max={balanceAtVenue} value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, Math.min(balanceAtVenue, parseInt(e.target.value) || 1)))}
                          className="w-16 text-center" data-testid="mark-used-quantity-input"
                        />
                        <button
                          type="button"
                          onClick={() => setQuantity((q) => Math.min(balanceAtVenue, q + 1))}
                          className="w-8 h-8 rounded-md border border-border flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-40"
                          disabled={quantity >= balanceAtVenue}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-xs text-muted-foreground">of {balanceAtVenue} available</span>
                      </div>
                    </div>
                  </>
                )}
              </>
            )
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !selectedClient || !venueId || balanceAtVenue <= 0} data-testid="mark-used-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Mark as Used
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkDayPassUsedModal;
